import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import TestSuiteSwimlanes from '../components/TestSuiteSwimlanes';
import TicketDetailModal from '../components/TicketDetailModal';
import UserManagement from '../components/UserManagement';
import CompanyManagement from '../components/CompanyManagement';
import Downloads from '../components/Downloads';
import Certifications from '../components/Certifications';
import CertificationStatusTable from '../components/CertificationStatusTable';
import UserReportTable from '../components/CertificationReportTable';
import HealthMonitor from '../components/HealthMonitor';
import Emails from '../components/Emails';
import FAQ from '../components/FAQ';
import CompanyPurge from '../components/CompanyPurge';
import NotificationBell from '../components/NotificationBell';
import PageLoader from '../components/PageLoader';
import { DataRefreshProvider } from '../contexts/DataRefreshContext';
import { authStorage } from '../services/authStorage';
import { authApi, ApiError } from '../services/authApi';
import { Company as ApiCompany, TestSuite, TestDetails, Receipt } from '../types/auth';
import { mapStatusToAPI, mapStatusFromAPI, formatStatusForDisplay } from '../utils/statusUtils';
import { processOptionalTest } from '../utils/optionalTestUtils';
import { getAvatarUrl } from '../utils/avatarUtils';
import { formatDate } from '../utils/dateUtils';
import '../styles/AdminViewPage.css';

interface TestTicket {
  id: number;
  ticketNumber: string;
  status: 'todo' | 'inProgress' | 'passed' | 'failed';
  description?: string;
  date: string;
  vatRate: string;
  requiresUpload?: boolean;
  assignee?: {
    name: string;
    avatar: string;
  };
  testSuiteRuleMappingId?: number;
  testNumber?: number;
  receipt?: Receipt | null;
  testName?: string;
}

interface EnhancedCompany extends ApiCompany {
  hasTestSuite: boolean;
  testSuite?: TestSuite;
  testDetails?: TestDetails;
  testRuleTickets?: TestTicket[];
  showCreateTestSuiteButton?: boolean;
}

interface AdminComment {
  id: number;
  author: {
    name: string;
    avatar: string;
  };
  date: string;
  text: string;
  status?: string;
}

interface AdminViewPageProps {
  onLogout?: () => void;
  currentUser?: {
    username: string;
    role: 'user' | 'admin';
  };
}

type CertificationStatusValue = 'CERTIFIED' | 'IN_PROGRESS' | 'NOT_STARTED';

type Section = 'testsuite' | 'users' | 'companies' | 'downloads' | 'certificates' | 'certificationStatus' | 'userReport' | 'health' | 'emails' | 'companyInfo' | 'faq' | 'purge';

const AdminViewPage: React.FC<AdminViewPageProps> = ({ onLogout, currentUser }) => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState<Section>('testsuite');
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [certificateFilterCompanyId, setCertificateFilterCompanyId] = useState<number | null>(null);
  const [returnSection, setReturnSection] = useState<Section | null>(null);
  const [companyInfoId, setCompanyInfoId] = useState<number | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<TestTicket | null>(null);
  const [ticketComments, setTicketComments] = useState<AdminComment[]>([]);
  const [showCreateTestSuiteModal, setShowCreateTestSuiteModal] = useState(false);
  const [creatingTestSuiteCompanyId, setCreatingTestSuiteCompanyId] = useState<number | null>(null);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [companySearchQuery, setCompanySearchQuery] = useState('');

  // API-driven state
  const [companies, setCompanies] = useState<EnhancedCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [notificationRefreshTrigger, setNotificationRefreshTrigger] = useState(false);
  const [isProcessingOptionalTest, setIsProcessingOptionalTest] = useState(false);
  const [showUsersExpanded, setShowUsersExpanded] = useState(false);
  const [showEditCompanyModal, setShowEditCompanyModal] = useState(false);
  const [editCompanyForm, setEditCompanyForm] = useState<{ id: number | null; companyName: string; orgNumber: string; isActive: boolean }>({ id: null, companyName: '', orgNumber: '', isActive: true });
  const [isSavingCompany, setIsSavingCompany] = useState(false);
  const [editCompanyError, setEditCompanyError] = useState('');
  const loadingCompanyTestSuitesRef = useRef<Set<number>>(new Set());
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Computed values
  const selectedCompanyData = selectedCompanyId ? companies.find(c => c.id === selectedCompanyId) : null;

  // Handle cache clear
  const handleClearCache = () => {
    if (window.confirm('Are you sure you want to clear the application cache? This will refresh all data and may log you out.')) {
      // Clear all localStorage data
      localStorage.clear();
      // Clear all sessionStorage data
      sessionStorage.clear();
      // Reload the page to refresh everything
      window.location.reload();
    }
  };

  // Filter companies based on search query and sort by company name
  const filteredCompanies = companies.filter(company =>
    company.companyName.toLowerCase().includes(companySearchQuery.toLowerCase())
  ).sort((a, b) => a.companyName.localeCompare(b.companyName));

  // Initialize component
  useEffect(() => {
    initializeAdminView();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        closeCompanyDropdown();
      }
    };

    if (showCompanyDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCompanyDropdown]);

  const initializeAdminView = async () => {
    try {
      setLoading(true);
      setError('');

      const userData = authStorage.getUserData();
      if (!userData || userData.companyId !== null) {
        setError('Unauthorized access. Admin privileges required.');
        return;
      }

      await loadAllCompanies();
    } catch (error) {
      console.error('Failed to initialize admin view:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to initialize admin view. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAllCompanies = async () => {
    try {
      const token = authStorage.getAccessToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const companiesData = await authApi.getAllCompanies(token);
      const enhancedCompanies: EnhancedCompany[] = companiesData
        .filter(company => company.isActive)
        .map(company => ({
          ...company,
          hasTestSuite: false
        }));

      setCompanies(enhancedCompanies);

      // For admin users, load test suites and details for all companies on initial load
      if (enhancedCompanies.length > 0) {
        await loadAllCompanyTestSuites(enhancedCompanies, token);

        // Select the first company by default
        const firstCompany = enhancedCompanies[0];
        setSelectedCompanyId(firstCompany.id);
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to load companies. Please try again.');
      }
    }
  };

  const loadAllCompanyTestSuites = async (companiesList: EnhancedCompany[], token: string) => {
    try {
      // Load test suites for all companies in parallel
      const testSuitePromises = companiesList.map(async (company) => {
        try {
          const testSuiteData = await authApi.getTestSuitesByCompany(token, company.id);

          // Always show test suites where isHistory = false first
          const activeTestSuites = testSuiteData.testSuites.filter(ts => !ts.isHistory);
          let selectedTestSuite: TestSuite | undefined = undefined;
          let hasTestSuite = false;
          let showCreateButton = false;

          if (activeTestSuites.length > 0) {
            // Show active test suites (should typically be only one active at a time)
            selectedTestSuite = activeTestSuites[0];
            hasTestSuite = true;
            showCreateButton = false;
          } else {
            // If response has "testSuites":[] or all test suites have isHistory = true,
            // then show the last test suite and create button for admins only
            const historicalTestSuites = testSuiteData.testSuites.filter(ts => ts.isHistory);

            if (historicalTestSuites.length > 0) {
              // Show the last historical test suite
              selectedTestSuite = historicalTestSuites[historicalTestSuites.length - 1];
              hasTestSuite = true;
              showCreateButton = true; // Show create button for admins only when viewing historical
            } else {
              // No test suites found at all
              selectedTestSuite = undefined;
              hasTestSuite = false;
              showCreateButton = true; // Show create button for admins only
            }
          }

          // Load test details if test suite exists
          let testDetails: TestDetails | undefined = undefined;
          let testRuleTickets: TestTicket[] | undefined = undefined;

          if (selectedTestSuite) {
            try {
              testDetails = await authApi.getTestDetails(token, selectedTestSuite.id);

              // Convert test rules to tickets
              testRuleTickets = testDetails.testRules.map(rule => {
                const vatMatch = rule.testName.match(/(\d+%)/g);
                const vatRate = vatMatch ? vatMatch.join(', ') : rule.testName;

                // Determine status based on receipt and its last status
                let status: 'todo' | 'inProgress' | 'passed' | 'failed' = 'todo';
                let assigneeInfo = undefined;
                let ticketNumber = 'Upload Test';

                if (rule.receipt) {
                  ticketNumber = 'Test Uploaded';
                  assigneeInfo = {
                    name: rule.receipt.uploadedByUser.username,
                    avatar: getAvatarUrl(rule.receipt.uploadedByUser.profilePicUrl, rule.receipt.uploadedByUser.username)
                  };

                  // If there's a testedByUser, use that as the assignee instead
                  if (rule.receipt.testedByUser) {
                    assigneeInfo = {
                      name: rule.receipt.testedByUser.username,
                      avatar: getAvatarUrl(rule.receipt.testedByUser.profileImageUrl, rule.receipt.testedByUser.username)
                    };
                  }

                  // Map the receipt's last status using the utility function
                  status = mapStatusFromAPI(rule.receipt.lastStatus) as 'todo' | 'inProgress' | 'passed' | 'failed';

                  // If mapping fails or returns invalid status, check events for more recent status
                  if (!['todo', 'inProgress', 'passed', 'failed'].includes(status)) {
                    if (rule.receipt.events && rule.receipt.events.length > 0) {
                      // Find the most recent status update event
                      const statusEvents = rule.receipt.events
                        .filter(event => event.eventName === 'Updated_Status' && event.status)
                        .sort((a, b) => new Date(b.commentTimestamp || '').getTime() - new Date(a.commentTimestamp || '').getTime());

                      if (statusEvents.length > 0) {
                        status = mapStatusFromAPI(statusEvents[0].status) as 'todo' | 'inProgress' | 'passed' | 'failed';
                      }
                    }

                    // Final fallback - if we still don't have a valid status, default based on receipt presence
                    if (!['todo', 'inProgress', 'passed', 'failed'].includes(status)) {
                      status = 'inProgress'; // Since receipt exists, assume it's in progress
                    }
                  }
                }

                return {
                  id: rule.testNumber,
                  ticketNumber,
                  status,
                  date: testDetails!.lastUpdatedUser.lastUpdatedDate.split(' ')[0],
                  vatRate,
                  requiresUpload: !rule.receipt,
                  assignee: assigneeInfo,
                  description: rule.testDescription,
                  testSuiteRuleMappingId: rule.receipt ? rule.receipt.testSuiteRuleMappingId : rule.testRuleInfoId,
                  testNumber: rule.testNumber,
                  receipt: rule.receipt,
                  testName: rule.testName
                };
              });
            } catch (error) {
              console.error(`Failed to load test details for company ${company.id}:`, error);
            }
          }

          return {
            companyId: company.id,
            hasTestSuite,
            testSuite: selectedTestSuite,
            testDetails,
            testRuleTickets,
            showCreateTestSuiteButton: showCreateButton
          };
        } catch (error) {
          console.error(`Failed to load test suite for company ${company.id}:`, error);
          return {
            companyId: company.id,
            hasTestSuite: false,
            testSuite: undefined,
            testDetails: undefined,
            testRuleTickets: undefined,
            showCreateTestSuiteButton: true
          };
        }
      });

      const testSuiteResults = await Promise.all(testSuitePromises);

      // Update companies with test suite data
      setCompanies(prevCompanies =>
        prevCompanies.map(company => {
          const result = testSuiteResults.find(r => r.companyId === company.id);
          if (result) {
            return {
              ...company,
              hasTestSuite: result.hasTestSuite,
              testSuite: result.testSuite,
              testDetails: result.testDetails,
              testRuleTickets: result.testRuleTickets,
              showCreateTestSuiteButton: result.showCreateTestSuiteButton
            };
          }
          return company;
        })
      );
    } catch (error) {
      console.error('Failed to load company test suites:', error);
    }
  };

  const loadCompanyTestSuite = async (companyId: number) => {
    try {
      const token = authStorage.getAccessToken();
      if (!token) return;

      // Check if we're already loading or already have complete test suite data for this company
      const existingCompany = companies.find(c => c.id === companyId);
      if (existingCompany && existingCompany.testSuite && existingCompany.testRuleTickets) return;

      if (loadingCompanyTestSuitesRef.current.has(companyId)) return;

      loadingCompanyTestSuitesRef.current.add(companyId);

      const testSuiteData = await authApi.getTestSuitesByCompany(token, companyId);

      // Always show test suites where isHistory = false first
      const activeTestSuites = testSuiteData.testSuites.filter(ts => !ts.isHistory);
      let selectedTestSuite: TestSuite | undefined = undefined;
      let hasTestSuite = false;
      let showCreateButton = false;

      if (activeTestSuites.length > 0) {
        // Show active test suites (should typically be only one active at a time)
        selectedTestSuite = activeTestSuites[0];
        hasTestSuite = true;
        showCreateButton = false;
      } else {
        // If response has "testSuites":[] or all test suites have isHistory = true,
        // then show the last test suite and create button for admins only
        const historicalTestSuites = testSuiteData.testSuites.filter(ts => ts.isHistory);

        if (historicalTestSuites.length > 0) {
          // Show the last historical test suite
          selectedTestSuite = historicalTestSuites[historicalTestSuites.length - 1];
          hasTestSuite = true;
          showCreateButton = true; // Show create button for admins only when viewing historical
        } else {
          // No test suites found at all
          selectedTestSuite = undefined;
          hasTestSuite = false;
          showCreateButton = true; // Show create button for admins only
        }
      }

      // Update the company with test suite info
      setCompanies(prevCompanies =>
        prevCompanies.map(company => {
          if (company.id === companyId) {
            return {
              ...company,
              hasTestSuite,
              testSuite: selectedTestSuite,
              showCreateTestSuiteButton: showCreateButton
            };
          }
          return company;
        })
      );

      // If test suite exists, load test details
      if (selectedTestSuite) {
        await loadTestDetails(companyId, selectedTestSuite.id);
      }
    } catch (error) {
      console.error('Failed to load company test suite:', error);
    } finally {
      loadingCompanyTestSuitesRef.current.delete(companyId);
    }
  };

  const loadTestDetails = async (companyId: number, testSuiteId: number) => {
    try {
      const token = authStorage.getAccessToken();
      if (!token) return;

      const testDetails = await authApi.getTestDetails(token, testSuiteId);

      // Convert test rules to tickets
      const testRuleTickets: TestTicket[] = testDetails.testRules.map(rule => {
        const vatMatch = rule.testName.match(/(\d+%)/g);
        const vatRate = vatMatch ? vatMatch.join(', ') : rule.testName;

        // Determine status based on receipt and its last status
        let status: 'todo' | 'inProgress' | 'passed' | 'failed' = 'todo';
        let assigneeInfo = undefined;
        let ticketNumber = 'Upload Test';

        if (rule.receipt) {
          ticketNumber = 'Test Uploaded';
          assigneeInfo = {
            name: rule.receipt.uploadedByUser.username,
            avatar: getAvatarUrl(rule.receipt.uploadedByUser.profilePicUrl, rule.receipt.uploadedByUser.username)
          };

          // If there's a testedByUser, use that as the assignee instead
          if (rule.receipt.testedByUser) {
            assigneeInfo = {
              name: rule.receipt.testedByUser.username,
              avatar: getAvatarUrl(rule.receipt.testedByUser.profileImageUrl, rule.receipt.testedByUser.username)
            };
          }

          // Map the receipt's last status using the utility function
          status = mapStatusFromAPI(rule.receipt.lastStatus) as 'todo' | 'inProgress' | 'passed' | 'failed';

          // If mapping fails or returns invalid status, check events for more recent status
          if (!['todo', 'inProgress', 'passed', 'failed'].includes(status)) {
            if (rule.receipt.events && rule.receipt.events.length > 0) {
              // Find the most recent status update event
              const statusEvents = rule.receipt.events
                .filter(event => event.eventName === 'Updated_Status' && event.status)
                .sort((a, b) => new Date(b.commentTimestamp || '').getTime() - new Date(a.commentTimestamp || '').getTime());

              if (statusEvents.length > 0) {
                status = mapStatusFromAPI(statusEvents[0].status) as 'todo' | 'inProgress' | 'passed' | 'failed';
              }
            }

            // Final fallback - if we still don't have a valid status, default based on receipt presence
            if (!['todo', 'inProgress', 'passed', 'failed'].includes(status)) {
              status = 'inProgress'; // Since receipt exists, assume it's in progress
            }
          }
        }

        return {
          id: rule.testNumber,
          ticketNumber,
          status,
          date: testDetails.lastUpdatedUser.lastUpdatedDate.split(' ')[0],
          vatRate,
          requiresUpload: !rule.receipt,
          assignee: assigneeInfo,
          description: rule.testDescription,
          testSuiteRuleMappingId: rule.receipt ? rule.receipt.testSuiteRuleMappingId : rule.testRuleInfoId,
          testNumber: rule.testNumber,
          receipt: rule.receipt,
          testName: rule.testName
        };
      });

      // Update the company with test details
      setCompanies(prevCompanies =>
        prevCompanies.map(company => {
          if (company.id === companyId) {
            return {
              ...company,
              testDetails,
              testRuleTickets
            };
          }
          return company;
        })
      );
    } catch (error) {
      console.error('Failed to load test details:', error);
    }
  };

  const handleCreateTestSuite = async (companyId: number) => {
    try {
      setError('');
      const token = authStorage.getAccessToken();
      const userData = authStorage.getUserData();

      if (!token || !userData) {
        setError('Authentication data not found. Please log in again.');
        return;
      }

      await authApi.createTestSuite(token, {
        companyId,
        lastUpdatedBy: userData.id
      });

      // Reload the test suite data for this company
      await loadCompanyTestSuite(companyId);

      // Close the modal
      setShowCreateTestSuiteModal(false);
      setCreatingTestSuiteCompanyId(null);
    } catch (error) {
      console.error('Failed to create test suite:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to create test suite. Please try again.');
      }
    }
  };

  const openCreateTestSuiteModal = (companyId: number) => {
    setCreatingTestSuiteCompanyId(companyId);
    setShowCreateTestSuiteModal(true);
  };

  const closeCreateTestSuiteModal = () => {
    setShowCreateTestSuiteModal(false);
    setCreatingTestSuiteCompanyId(null);
  };

  // Data refresh functions for context
  const refreshCompanies = async () => {
    try {
      setError('');
      await loadAllCompanies();
    } catch (error) {
      console.error('Failed to refresh companies:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to refresh companies. Please try again.');
      }
    }
  };

  const refreshUsers = async () => {
    // Users are part of companies data, so refresh companies
    await refreshCompanies();
  };

  const refreshAllData = async () => {
    await refreshCompanies();
  };

  const selectCompany = (companyId: number) => {
    setSelectedCompanyId(companyId);
  };

  const closeCompanyDropdown = () => {
    setShowCompanyDropdown(false);
    setCompanySearchQuery('');
  };

  const goBack = () => {
    if (returnSection) {
      setActiveSection(returnSection);
      setReturnSection(null);
      setCertificateFilterCompanyId(null);
      setCompanyInfoId(null);
      setShowUsersExpanded(false);
    }
  };

  const handleStatusRedirect = (company: { id: number; certificationStatus: CertificationStatusValue }) => {
    setReturnSection('certificationStatus');
    setSelectedCompanyId(company.id);

    if (company.certificationStatus === 'CERTIFIED') {
      setCertificateFilterCompanyId(company.id);
      setActiveSection('certificates');
    } else {
      setCertificateFilterCompanyId(null);
      setActiveSection('testsuite');
    }
  };

  const handleCompanyInfo = (company: { id: number }, origin: Section = 'certificationStatus') => {
    setCompanyInfoId(company.id);
    setReturnSection(origin);
    setActiveSection('companyInfo');
  };

  const handleNotificationCompanySelect = (companyId: number) => {
    // Switch to test suite section
    setActiveSection('testsuite');
    // Select the company
    setSelectedCompanyId(companyId);
    // Trigger notification refresh
    setNotificationRefreshTrigger(prev => !prev);
  };

  const handleTicketClick = (ticket: TestTicket) => {
    // Admin users can click on any ticket (with or without receipts) to view details or upload receipts
    // This is different from TestSuitePage where admins could only click on tickets with receipts
    setSelectedTicket(ticket);

    // Generate comments from receipt events (same logic as TestSuitePage)
    const comments: AdminComment[] = [];
    let commentId = 1;

    if (ticket.receipt && ticket.receipt.events) {
      ticket.receipt.events.forEach(event => {
        // Map event names to user-friendly text
        let eventText = event.comment || 'Updated status';
        if (event.eventName === 'Uploaded_Receipt') {
          eventText = `Uploaded receipt for ${ticket.vatRate}`;
        } else if (event.eventName === 'Updated_Status') {
          eventText = event.comment || `Status updated to ${event.status.replace('_', ' ')}`;
        } else if (event.eventName === 'Added_Comment') {
          eventText = event.comment || 'Added a comment';
        }

        comments.push({
          id: commentId++,
          author: {
            name: event.commentedByUser?.username || ticket.receipt!.uploadedByUser.username,
            avatar: event.commentedByUser?.profileImageUrl
              ? getAvatarUrl(event.commentedByUser.profileImageUrl, event.commentedByUser.username)
              : getAvatarUrl(ticket.receipt!.uploadedByUser.profilePicUrl, ticket.receipt!.uploadedByUser.username)
          },
          date: event.commentTimestamp?.split(' ')[0] || ticket.receipt!.uploadedTimestamp.split(' ')[0],
          text: eventText,
          status: event.eventName === 'Updated_Status' ? event.status : undefined
        });

        // Add child events if they exist
        if (event.childEvents && event.childEvents.length > 0) {
          event.childEvents.forEach(childEvent => {
            if (childEvent.comment && childEvent.commentedByUser) {
              comments.push({
                id: commentId++,
                author: {
                  name: childEvent.commentedByUser.username,
                  avatar: getAvatarUrl(childEvent.commentedByUser.profileImageUrl, childEvent.commentedByUser.username)
                },
                date: childEvent.commentTimestamp?.split(' ')[0] || ticket.date,
                text: childEvent.comment,
                status: childEvent.eventName === 'Updated_Status' ? childEvent.status : undefined
              });
            }
          });
        }
      });
    }

    setTicketComments(comments);
  };

  const handleTicketDrop = async (ticketId: number, newStatus: 'todo' | 'inProgress' | 'passed' | 'failed') => {
    if (!selectedCompanyId) return;

    try {
      const token = authStorage.getAccessToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      // Find the ticket to get its testSuiteRuleMappingId
      const company = companies.find(c => c.id === selectedCompanyId);
      const ticket = company?.testRuleTickets?.find(t => t.id === ticketId);
      if (!ticket) {
        console.error('Ticket not found:', ticketId);
        return;
      }

      const mappingId = ticket.testSuiteRuleMappingId || ticket.testNumber || ticket.id;

      // Call updateTest API
      await authApi.updateTest(token, {
        testSuiteRuleMappingId: mappingId,
        status: mapStatusToAPI(newStatus),
        comment: `Status updated to ${formatStatusForDisplay(newStatus)}`,
        companyId: selectedCompanyId || undefined, // Pass the selected company ID for admin users
      });

      // Update local state only after successful API call
      setCompanies(prevCompanies =>
        prevCompanies.map(company => {
          if (company.id === selectedCompanyId && company.testRuleTickets) {
            const updatedTickets = company.testRuleTickets.map((ticket: TestTicket) =>
              ticket.id === ticketId ? { ...ticket, status: newStatus } : ticket
            );

            return {
              ...company,
              testRuleTickets: updatedTickets
            };
          }
          return company;
        })
      );

      // Reload test details to get updated data from server
      if (company?.testSuite) {
        await loadTestDetails(selectedCompanyId, company.testSuite.id);
      }

      // Refresh notifications to show updated status
      setNotificationRefreshTrigger(prev => !prev);
    } catch (error) {
      console.error('Failed to update test status:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to update test status. Please try again.');
      }
    }
  };

  const handleMarkAsOptional = async (ticket: TestTicket) => {
    if (isProcessingOptionalTest) return; // Prevent multiple simultaneous processing

    try {
      setIsProcessingOptionalTest(true);
      setError(''); // Clear any previous errors

      await processOptionalTest(ticket, selectedCompanyId || undefined);

      // Refresh the test details for the selected company
      if (selectedCompanyId) {
        const company = companies.find(c => c.id === selectedCompanyId);
        if (company?.testSuite) {
          await loadTestDetails(selectedCompanyId, company.testSuite.id);
        }
      }

      // Refresh notifications to show updated status
      setNotificationRefreshTrigger(prev => !prev);
    } catch (error) {
      console.error('Failed to mark test as optional:', error);
      setError(error instanceof Error ? error.message : 'Failed to mark test as optional');
    } finally {
      setIsProcessingOptionalTest(false);
    }
  };

  const openEditCompanyModal = (company: EnhancedCompany) => {
    setEditCompanyError('');
    setEditCompanyForm({
      id: company.id,
      companyName: company.companyName,
      orgNumber: company.orgNumber,
      isActive: company.isActive,
    });
    setShowEditCompanyModal(true);
  };

  const closeEditCompanyModal = () => {
    setShowEditCompanyModal(false);
    setEditCompanyForm({ id: null, companyName: '', orgNumber: '', isActive: true });
  };

  const handleEditCompanyChange = (field: 'companyName' | 'orgNumber' | 'isActive', value: string | boolean) => {
    setEditCompanyForm(prev => ({ ...prev, [field]: value } as typeof prev));
  };

  const handleSaveCompany = async () => {
    if (!editCompanyForm.id) return;

    try {
      setIsSavingCompany(true);
      setEditCompanyError('');
      const token = authStorage.getAccessToken();
      if (!token) {
        setEditCompanyError('Authentication token not found. Please log in again.');
        return;
      }

      await authApi.updateCompany(token, editCompanyForm.id, {
        companyName: editCompanyForm.companyName,
        orgNumber: editCompanyForm.orgNumber,
        isActive: editCompanyForm.isActive,
      });

      setCompanies(prev => prev.map(c => {
        if (c.id !== editCompanyForm.id) return c;
        return {
          ...c,
          companyName: editCompanyForm.companyName,
          orgNumber: editCompanyForm.orgNumber,
          isActive: editCompanyForm.isActive,
        };
      }));
      await refreshCompanies();
      closeEditCompanyModal();
    } catch (error) {
      console.error('Failed to update company:', error);
      if (error instanceof ApiError) {
        setEditCompanyError(error.message);
      } else {
        setEditCompanyError('Failed to update company. Please try again.');
      }
    } finally {
      setIsSavingCompany(false);
    }
  };

  const getStatusCounts = (company: EnhancedCompany) => {
    if (!company.testRuleTickets) {
      return { todo: 0, inProgress: 0, passed: 0, failed: 0 };
    }

    return {
      todo: company.testRuleTickets.filter(t => t.status === 'todo').length,
      inProgress: company.testRuleTickets.filter(t => t.status === 'inProgress').length,
      passed: company.testRuleTickets.filter(t => t.status === 'passed').length,
      failed: company.testRuleTickets.filter(t => t.status === 'failed').length,
    };
  };

  const getCompanyStatus = (company: EnhancedCompany) => {
    if (!company.hasTestSuite) return 'no-suite';

    const statusCounts = getStatusCounts(company);
    if (statusCounts.failed > 0) return 'has-failed';
    if (statusCounts.inProgress > 0) return 'in-progress';
    if (statusCounts.todo > 0) return 'pending';
    return 'completed';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'green';
      case 'in-progress': return 'yellow';
      case 'has-failed': return 'red';
      case 'pending': return 'yellow';
      default: return 'gray';
    }
  };

  const getCompanyImageUrl = (company: EnhancedCompany | null) => {
    if (!company) return '';

    const logoCandidate =
      company.logoUrl ||
      company.companyLogo ||
      (company as { companyLogoUrl?: string | null }).companyLogoUrl ||
      (company as { companyLogoURL?: string | null }).companyLogoURL ||
      (company as { logo?: string | null }).logo ||
      (company as { logoURL?: string | null }).logoURL ||
      (company as { logoUrl?: string | null }).logoUrl ||
      (company as { companyImageUrl?: string | null }).companyImageUrl ||
      (company as { imageUrl?: string | null }).imageUrl ||
      company.profileImageUrl ||
      null;

    if (logoCandidate && logoCandidate.trim() !== '') {
      return logoCandidate;
    }

    return getAvatarUrl(null, company.companyName);
  };

  const renderBackBar = () => {
    if (!returnSection) return null;
    return (
      <div className="back-bar">
        <button className="back-button" onClick={goBack}>← Go back</button>
      </div>
    );
  };

  const renderMainContent = () => {
    switch (activeSection) {
      case 'users':
        return (
          <div className="section-content">
            {/* <h1>{t('admin.users')}</h1> */}
            <DataRefreshProvider
              refreshCompanies={refreshCompanies}
              refreshUsers={refreshUsers}
              refreshAllData={refreshAllData}
            >
              <UserManagement />
            </DataRefreshProvider>
          </div>
        );
      case 'companies':
        return (
          <div className="section-content">
            {/* <h1>{t('admin.companies')}</h1> */}
            <DataRefreshProvider
              refreshCompanies={refreshCompanies}
              refreshUsers={refreshUsers}
              refreshAllData={refreshAllData}
            >
              <CompanyManagement
                onCompanyClick={(company) => handleCompanyInfo(company, 'companies')}
                onViewTestSuite={(company) => {
                  setSelectedCompanyId(company.id);
                  setReturnSection(null);
                  setActiveSection('testsuite');
                }}
              />
            </DataRefreshProvider>
          </div>
        );
      case 'downloads':
        return (
          <div className="section-content">
            <h1>{t('downloads.title')}</h1>
            <Downloads isAdmin={true} currentUser={currentUser} showTitle={false} />
          </div>
        );
      case 'certificates':
        return (
          <div className="section-content">
            {renderBackBar()}
            <h1>{t('certificates.title') || 'Certificates'}</h1>
            <Certifications
              isAdmin={true}
              currentUser={currentUser}
              showTitle={false}
              companyId={certificateFilterCompanyId ?? undefined}
            />
          </div>
        );
      case 'certificationStatus':
        return (
          <div className="section-content" style={{ padding: 0, height: '100%' }}>
            <CertificationStatusTable
              onStatusClick={handleStatusRedirect}
              onCompanyClick={(company) => handleCompanyInfo(company, 'certificationStatus')}
            />
          </div>
        );
      case 'userReport':
        return (
          <div className="section-content" style={{ padding: 0, height: '100%' }}>
            <UserReportTable />
          </div>
        );
      case 'health':
        return (
          <div className="section-content">
            <HealthMonitor currentUser={currentUser} />
          </div>
        );
      case 'emails':
        return (
          <div className="section-content">
            <h1>Send Emails</h1>
            <Emails currentUser={currentUser} />
          </div>
        );
      case 'faq':
        return (
          <div className="section-content">
            <FAQ isAdmin={true} />
          </div>
        );
      case 'purge':
        return (
          <div className="section-content">
            <DataRefreshProvider
              refreshCompanies={refreshCompanies}
              refreshUsers={refreshUsers}
              refreshAllData={refreshAllData}
            >
              <CompanyPurge />
            </DataRefreshProvider>
          </div>
        );
      case 'companyInfo': {
        const companyInfo = companies.find(c => c.id === companyInfoId) || null;
        return (
          <div className="section-content">
            {renderBackBar()}
            <h1>Company Info</h1>
            {companyInfo ? (
              <div className="company-info-card">
                <div className="company-actions">
                  <button
                    className="action-button view"
                    onClick={() => openEditCompanyModal(companyInfo)}
                    aria-label="Edit company"
                    title="Edit company"
                  >
                    <span className="action-icon">✏️</span>
                  </button>
                  <button
                    className="action-button edit"
                    onClick={() => setShowUsersExpanded(prev => !prev)}
                    aria-label={showUsersExpanded ? 'Hide users' : 'Show users'}
                    title={showUsersExpanded ? 'Hide users' : 'Show users'}
                  >
                    <span className="action-icon">👥</span>
                  </button>
                </div>
                <div className="company-info-row">
                  <span className="company-info-label">Name</span>
                  <span className="company-info-value">{companyInfo.companyName}</span>
                </div>
                <div className="company-info-row">
                  <span className="company-info-label">Org Number</span>
                  <span className="company-info-value">{companyInfo.orgNumber}</span>
                </div>
                <div className="company-info-row">
                  <span className="company-info-label">Status</span>
                  <span className="company-info-value">{companyInfo.isActive ? 'Active' : 'Inactive'}</span>
                </div>
                <div className="company-info-row">
                  <span className="company-info-label">Last Updated</span>
                  <span className="company-info-value">{formatDate(companyInfo.lastUpdatedDate)}</span>
                </div>
                {showUsersExpanded && (
                  <div className="company-users">
                    <div className="company-users-header">Users</div>
                    {companyInfo.users && companyInfo.users.length > 0 ? (
                      <ul className="company-users-list">
                        {companyInfo.users.map(user => (
                          <li key={user.id} className="company-user-item">
                            <img src={getAvatarUrl(user.profileImageUrl, user.username)} alt={user.username} />
                            <div className="company-user-meta">
                              <span className="company-user-name">{user.username}</span>
                              <span className="company-user-email">{user.email}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="no-users">No users found for this company.</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p>No company information available.</p>
            )}
          </div>
        );
      }
      case 'testsuite':
      default:
        return (
          <div className="testsuite-section">
            {renderBackBar()}
            <div className="testsuite-header">
              <h1>{t('admin.activeTestSuite')}</h1>
              <div className="company-dropdown-container" ref={dropdownRef}>
                <button
                  className="company-dropdown-trigger"
                  onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
                >
                  {selectedCompanyData ? selectedCompanyData.companyName : t('admin.selectCompany', 'Select a company')}
                  <span className={`dropdown-arrow ${showCompanyDropdown ? 'open' : ''}`}>▼</span>
                </button>

                {showCompanyDropdown && (
                  <div className="company-dropdown-popup">
                    <div className="company-dropdown-content">
                      <div className="company-search-container">
                        <input
                          type="text"
                          placeholder={t('admin.searchCompanies', 'Search companies...') || 'Search companies...'}
                          value={companySearchQuery}
                          onChange={(e) => setCompanySearchQuery(e.target.value)}
                          className="company-search-input"
                          autoFocus
                        />
                        <button
                          className="dropdown-close"
                          onClick={closeCompanyDropdown}
                        >
                          ×
                        </button>
                      </div>
                      <div className="company-dropdown-list">
                        {filteredCompanies.length > 0 ? filteredCompanies.map(company => {
                          const statusCounts = getStatusCounts(company);
                          const companyStatus = getCompanyStatus(company);
                          const statusColor = getStatusColor(companyStatus);

                          return (
                            <div key={company.id} className="company-dropdown-item">
                              <div
                                className={`company-item-content ${selectedCompanyId === company.id ? 'selected' : ''}`}
                                onClick={() => {
                                  selectCompany(company.id);
                                  closeCompanyDropdown();
                                }}
                              >
                                <div className={`status-indicator ${statusColor}`}></div>
                                <span className="company-name">{company.companyName}</span>
                                {company.hasTestSuite && (
                                  <div className="status-summary">
                                    <span className="status-count todo" title={`${statusCounts.todo} tasks to do`}>{statusCounts.todo}</span>
                                    <span className="status-count in-progress" title={`${statusCounts.inProgress} tasks in progress`}>{statusCounts.inProgress}</span>
                                    <span className="status-count passed" title={`${statusCounts.passed} tasks passed`}>{statusCounts.passed}</span>
                                    <span className="status-count failed" title={`${statusCounts.failed} tasks failed`}>{statusCounts.failed}</span>
                                  </div>
                                )}
                                {company.showCreateTestSuiteButton && (
                                  <button
                                    className="create-test-suite-btn compact"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openCreateTestSuiteModal(company.id);
                                      closeCompanyDropdown();
                                    }}
                                    title="Create Test Suite"
                                  >
                                    +
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        }) : (
                          <div className="no-companies">
                            <p>{companySearchQuery
                              ? (t('admin.noCompaniesFound', 'No companies found matching your search.') || 'No companies found matching your search.')
                              : (t('testSuite.noCompanies', 'No active companies found.') || 'No active companies found.')}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {loading ? (
              <div className="loading-section">
                <p>{t('common.loading', 'Loading companies...')}</p>
              </div>
            ) : error ? (
              <div className="error-section">
                <p className="error-message">{error}</p>
                <button onClick={initializeAdminView} className="retry-button">
                  {t('common.retry', 'Retry')}
                </button>
              </div>
            ) : (
              <div className="testsuite-content">
                {selectedCompanyData && selectedCompanyData.testRuleTickets ? (
                  <TestSuiteSwimlanes
                    tickets={selectedCompanyData.testRuleTickets}
                    onTicketClick={handleTicketClick}
                    onTicketDrop={handleTicketDrop}
                    showCounts={true}
                    isAdmin={true}
                    onMarkAsOptional={handleMarkAsOptional}
                  />
                ) : selectedCompanyData && selectedCompanyData.showCreateTestSuiteButton ? (
                  <div className="no-test-suite-selected">
                    <div className="empty-state">
                      <h3>{t('testSuite.noTestSuite', 'No Test Suite Found')}</h3>
                      <p>{selectedCompanyData.hasTestSuite
                        ? t('testSuite.historicalTestSuite', 'This company has historical test suites only. Create a new test suite to continue.')
                        : t('testSuite.noTestSuiteDesc', 'This company does not have an active test suite yet.')}</p>
                      <button
                        className="create-test-suite-btn primary"
                        onClick={() => openCreateTestSuiteModal(selectedCompanyData.id)}
                      >
                        {t('testSuite.createTestSuite', 'Create Test Suite')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="no-selection">
                    <p>{t('admin.selectCompany', 'Select a company to view their test suite')}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div className="admin-view-page">
      {/* <header className="admin-header">
        <div className="logo-container">
          <img src="https://infrasec.se/wp-content/uploads/2024/09/Infrasec_lockup_01@4x.png" alt="Infrasec" className="logo" />
        </div>
        <LanguageSelector />
        <NotificationBell 
            onSelectCompany={handleNotificationCompanySelect}
            refreshTrigger={notificationRefreshTrigger}
            isAdmin={true}
          />
        {currentUser && (
            <span className="user-name">
              {currentUser.username} ({t(`roles.${currentUser.role}`)})
            </span>
          )}
      </header> */}

      <header className="admin-header">
        <div className="logo-container">
          <img
            src="https://infrasec.se/wp-content/uploads/2024/09/Infrasec_lockup_01@4x.png"
            alt="Infrasec"
            className="logo"
          />
        </div>

        <div className="header-controls">
          {/* <LanguageSelector /> */}
          <button
            className="cache-clear-button"
            onClick={handleClearCache}
            title="Clear Application Cache"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41zm-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z" />
              <path fillRule="evenodd" d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5.002 5.002 0 0 0 8 3zM3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9H3.1z" />
            </svg>
            <span className="cache-button-text">Clear Cache</span>
          </button>
          <NotificationBell
            onSelectCompany={handleNotificationCompanySelect}
            refreshTrigger={notificationRefreshTrigger}
            isAdmin={true}
          />
          {currentUser && (
            <div className="user-info">
              <div className="avatar">
                <img src={getAvatarUrl(null, currentUser.username)} alt={currentUser.username} />
              </div>
              <span className="user-name">
                {currentUser.username} ({t(`roles.${currentUser.role}`)})
              </span>
            </div>
          )}
        </div>
      </header>

      <main className="admin-content">
        <div className="admin-main-layout">
          <div className="main-sidebar">
            <div className="sidebar-menu">
              <button
                className={`sidebar-button ${activeSection === 'testsuite' ? 'active' : ''}`}
                onClick={() => { setActiveSection('testsuite'); setReturnSection(null); setCertificateFilterCompanyId(null); setCompanyInfoId(null); }}
              >
                <span className="icon document-icon"></span>
                <span className="tooltip">{t('sidebar.testSuite')}</span>
              </button>
              <button
                className={`sidebar-button ${activeSection === 'users' ? 'active' : ''}`}
                onClick={() => { setActiveSection('users'); setReturnSection(null); setCertificateFilterCompanyId(null); setCompanyInfoId(null); }}
              >
                <span className="icon person-icon"></span>
                <span className="tooltip">{t('sidebar.users')}</span>
              </button>
              <button
                className={`sidebar-button ${activeSection === 'companies' ? 'active' : ''}`}
                onClick={() => { setActiveSection('companies'); setReturnSection(null); setCertificateFilterCompanyId(null); setCompanyInfoId(null); }}
              >
                <span className="icon company-icon"></span>
                <span className="tooltip">{t('sidebar.companies')}</span>
              </button>
              <button
                className={`sidebar-button ${activeSection === 'downloads' ? 'active' : ''}`}
                onClick={() => { setActiveSection('downloads'); setReturnSection(null); setCertificateFilterCompanyId(null); setCompanyInfoId(null); }}
              >
                <span className="icon download-icon"></span>
                <span className="tooltip">{t('sidebar.downloads')}</span>
              </button>
              <button
                className={`sidebar-button ${activeSection === 'certificates' ? 'active' : ''}`}
                onClick={() => {
                  setCertificateFilterCompanyId(null);
                  setReturnSection(null);
                  setCompanyInfoId(null);
                  setActiveSection('certificates');
                }}
              >
                <span className="icon certificate-icon"></span>
                <span className="tooltip">{t('sidebar.certificates')}</span>
              </button>
              <button
                className={`sidebar-button ${activeSection === 'certificationStatus' ? 'active' : ''}`}
                onClick={() => { setActiveSection('certificationStatus'); setReturnSection(null); setCertificateFilterCompanyId(null); setCompanyInfoId(null); }}
              >
                <span className="icon status-icon"></span>
                <span className="tooltip">Certification Status</span>
              </button>
              <button
                className={`sidebar-button ${activeSection === 'userReport' ? 'active' : ''}`}
                onClick={() => { setActiveSection('userReport'); setReturnSection(null); setCertificateFilterCompanyId(null); setCompanyInfoId(null); }}
              >
                <span className="icon user-report-icon"></span>
                <span className="tooltip">User Report</span>
              </button>
              <button
                className={`sidebar-button ${activeSection === 'health' ? 'active' : ''}`}
                onClick={() => { setActiveSection('health'); setReturnSection(null); setCertificateFilterCompanyId(null); setCompanyInfoId(null); }}
              >
                <span className="icon health-icon"></span>
                <span className="tooltip">System Health</span>
              </button>
              <button
                className={`sidebar-button ${activeSection === 'emails' ? 'active' : ''}`}
                onClick={() => { setActiveSection('emails'); setReturnSection(null); setCertificateFilterCompanyId(null); setCompanyInfoId(null); }}
              >
                <span className="icon email-icon"></span>
                <span className="tooltip">Emails</span>
              </button>
              <button
                className={`sidebar-button ${activeSection === 'faq' ? 'active' : ''}`}
                onClick={() => { setActiveSection('faq'); setReturnSection(null); setCertificateFilterCompanyId(null); setCompanyInfoId(null); }}
              >
                <span className="icon faq-icon"></span>
                <span className="tooltip">FAQ</span>
              </button>
              <button
                className={`sidebar-button danger-sidebar-button ${activeSection === 'purge' ? 'active' : ''}`}
                onClick={() => { setActiveSection('purge'); setReturnSection(null); setCertificateFilterCompanyId(null); setCompanyInfoId(null); }}
              >
                <span className="icon purge-icon"></span>
                <span className="tooltip">Permanent Deletion</span>
              </button>
              {onLogout && (
                <button className="sidebar-button logout-sidebar-button" onClick={onLogout}>
                  <span className="icon logout-icon"></span>
                  <span className="tooltip">{t('sidebar.logout')}</span>
                </button>
              )}
            </div>
          </div>

          <div className="content-area">
            {renderMainContent()}
          </div>
        </div>
      </main>

      {/* Create Test Suite Modal */}
      {showCreateTestSuiteModal && creatingTestSuiteCompanyId && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{t('testSuite.createTestSuite', 'Create Test Suite')}</h2>
              <button className="modal-close" onClick={closeCreateTestSuiteModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="create-test-suite-form">
                <p>{t('testSuite.createTestSuiteConfirm', 'Are you sure you want to create a test suite for this company?')}</p>
                <div className="company-info-modal">
                  <strong>Company: </strong>
                  {companies.find(c => c.id === creatingTestSuiteCompanyId)?.companyName}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeCreateTestSuiteModal}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                className="btn-primary"
                onClick={() => handleCreateTestSuite(creatingTestSuiteCompanyId)}
              >
                {t('testSuite.createTestSuite', 'Create Test Suite')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Company Modal */}
      {showEditCompanyModal && (
        <div className="modal-overlay">
          <div className="modal-content ">
            <div className="modal-header">
              <h2>Edit Company</h2>
              <button className="modal-close" onClick={closeEditCompanyModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Company Name</label>
                <input
                  type="text"
                  value={editCompanyForm.companyName}
                  onChange={(e) => handleEditCompanyChange('companyName', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Organization Number</label>
                <input
                  type="text"
                  value={editCompanyForm.orgNumber}
                  onChange={(e) => handleEditCompanyChange('orgNumber', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={editCompanyForm.isActive ? 'Active' : 'Inactive'}
                  onChange={(e) => handleEditCompanyChange('isActive', e.target.value === 'Active')}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              {editCompanyError && <p className="error-message" style={{ marginTop: 8 }}>{editCompanyError}</p>}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeEditCompanyModal} disabled={isSavingCompany}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSaveCompany} disabled={isSavingCompany}>
                {isSavingCompany ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          comments={ticketComments}
          onClose={() => setSelectedTicket(null)}
          companyId={selectedCompanyId || undefined} // Pass the selected company ID for admin users
          onMarkAsOptional={handleMarkAsOptional}
          onTestUpdate={() => {
            // Refresh the test details for the selected company
            if (selectedCompanyId) {
              const company = companies.find(c => c.id === selectedCompanyId);
              if (company?.testSuite) {
                loadTestDetails(selectedCompanyId, company.testSuite.id);
              }
            }
            // Refresh notifications to show updated status
            setNotificationRefreshTrigger(prev => !prev);
            setSelectedTicket(null);
          }}
        />
      )}

      {/* Page Loader for Optional Test Processing */}
      <PageLoader
        isLoading={isProcessingOptionalTest}
        message={t('testSuite.processingOptionalTest', 'Processing optional test...') || 'Processing optional test...'}
      />
    </div>
  );
};

export default AdminViewPage;